/**
 * Who can pick each outfit now, for the outfit gallery (components/DesktopMascotCard.tsx). Owner, 2026-10-13 ("fix
 * outfit labels"): the gallery said "In the gallery" and "Always in the gallery" of an outfit's date window, which read
 * as "everyone can pick it" while 34 of 39 sets were switched off and people could pick only the built-in Gyro, the
 * Halloween and NFL sets, and a day outfit on its day. The words here say WHO can pick an outfit: everyone, or the
 * principals only.
 *
 * The service's rule, followed here and never changed (services/gyro-svc/src/desktopMascot.ts looksView,
 * desktopMascotCategories.ts darkOutfitsOn, desktopMascotFullAccess.ts): the principals, the people under "Always see
 * every outfit" (the list IT keeps there and the USER-PRINCIPALS group), see every outfit that is not retired, whatever
 * its date window, its set or its day. Everyone else sees a day outfit on its day, and otherwise an outfit whose set
 * (its category, lib/outfitCategories.ts) is live today, switched on and in its season, General always, and whose own
 * date window is on now. People also keep an outfit they own whatever its window; the console cannot say who owns
 * what, so that is not counted here.
 *
 * No React, no network.
 */
import {
  effectiveWindow,
  outfitState,
  pacificDate,
  windowKey,
  type AdminDay,
  type AdminOutfit,
  type MascotWindow,
  type OutfitKind,
  type PacificDate,
} from './desktopMascot';
import { categoryLiveOn, categoryWindowText, monthDayNumber, monthDayParts, type CategoryWindow, type OutfitCategory } from './outfitCategories';

/** Why an outfit is seen by whom. */
export type OutfitVisibilityKind = 'everyone' | 'everyone-on-day' | 'set-off' | 'set-out-of-window' | 'scheduled' | 'ended' | 'retired' | 'built-in';
/** The filter's and the group chips' four words: who can pick the outfits, and for the principals-only ones, why in a word. */
export type OutfitBucket = 'everyone' | 'principals' | 'scheduled' | 'ended';
export type OutfitAudience = 'everyone' | 'principals' | 'nobody';

export interface OutfitVisibility {
  kind: OutfitVisibilityKind;
  audience: OutfitAudience;
  /** The chip's words: "Everyone now", "Principals only until Nov 1", "Principals only: AFC East is off", "Retired". */
  label: string;
  /** The selected outfit's sentence: the set and its switch or season, or the date window, in plain words. */
  why: string;
  /** Its set, when the registry knows it; null for General (no set, or a set the registry no longer has). */
  set: { id: string; name: string } | null;
}

/** What outfitVisibility reads of an outfit. */
export type VisibilityOutfit = Pick<AdminOutfit, 'id' | 'window'> & { kind?: OutfitKind; category?: string | null };

/** What is read of the categories registry (CategoriesView): each set's switch and season, and the set each outfit is in. */
export interface CategoriesLike {
  categories: ReadonlyArray<Pick<OutfitCategory, 'id' | 'name' | 'enabled' | 'window'>>;
  outfits: Readonly<Record<string, string>>;
}

export interface VisibilityOptions {
  /** The outfit is retired (catalog.retired names it): nobody can pick it. */
  retired?: boolean;
  /** A day outfit's rule (catalog.days[id]), for the day it comes next. */
  day?: Pick<AdminDay, 'nextDates'> | null;
  /** The browser's locale for the dates; the engine's default otherwise. */
  locale?: string;
}

/** The built-in Gyro's chip. */
export const BUILT_IN_LABEL = 'Everyone, always';
/** The gallery's intro sentence naming the principals, the same word the chips use. */
export const PRINCIPALS_HINT = 'The principals (the people under Always see every outfit, below) can pick every outfit at any time, whatever its set or date window.';

/** The filter each kind falls under; a retired outfit is in no group of the grid. */
export const VISIBILITY_BUCKET: Record<OutfitVisibilityKind, OutfitBucket | null> = {
  everyone: 'everyone',
  'everyone-on-day': 'everyone',
  'built-in': 'everyone',
  'set-off': 'principals',
  'set-out-of-window': 'principals',
  scheduled: 'scheduled',
  ended: 'ended',
  retired: null,
};

const dateFormats = new Map<string, Intl.DateTimeFormat>();

/** A calendar date in words, the year only when it is not this year in Pacific time: "Nov 1", "Jan 1, 2027". */
function dateWords(date: PacificDate, now: number, locale?: string): string {
  const withYear = date.year !== pacificDate(now).year;
  const key = `${locale ?? ''}|${withYear}`;
  let format = dateFormats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' as const } : {}) });
    if (dateFormats.size >= 16) dateFormats.clear();
    dateFormats.set(key, format);
  }
  return format.format(Date.UTC(date.year, date.month - 1, date.day));
}

/** A 'YYYY-MM-DD' (a day outfit's next day) as a date; undefined when it is not one. */
function isoDate(iso: string): PacificDate | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) } : undefined;
}

/** The next day a set's season starts, from today (Pacific): this year while its start is still ahead, else next year. */
export function nextSeasonStart(window: CategoryWindow, today: string, now: number): PacificDate {
  const { month, day } = monthDayParts(window.from);
  const year = pacificDate(now).year + (monthDayNumber(window.from) > monthDayNumber(today) ? 0 : 1);
  return { year, month, day };
}

const compareDates = (a: PacificDate, b: PacificDate): number => a.year - b.year || a.month - b.month || a.day - b.day;

/** The set an outfit is in, when the registry knows it: General, no registry, and a set the registry no longer has are null (never dark). */
function setOf(outfit: VisibilityOutfit, categories: CategoriesLike | null | undefined): CategoriesLike['categories'][number] | null {
  if (!categories) return null;
  const id = outfit.category !== undefined ? outfit.category : (categories.outfits[outfit.id] ?? null);
  return id ? categories.categories.find((category) => category.id === id) ?? null : null;
}

/**
 * Who can pick an outfit now, and why, as the service rules it: the built-in Gyro (everyone, always); a retired outfit
 * (nobody); a day outfit (everyone on its day); an outfit whose set is switched off (the principals only); one whose
 * own date window has ended (the principals only); one whose set is out of season or whose window has not started
 * (the principals only until the later of the two days); else everyone, now. `today` is the Pacific "MM-DD" the sets'
 * seasons are judged on (pacificMonthDay(now)); `now` judges the date windows.
 */
export function outfitVisibility(outfit: VisibilityOutfit | 'built-in', categories: CategoriesLike | null | undefined, today: string, now: number, options: VisibilityOptions = {}): OutfitVisibility {
  if (outfit === 'built-in') {
    return { kind: 'built-in', audience: 'everyone', label: BUILT_IN_LABEL, why: 'The built-in Gyro is in everyone’s gallery at all times and cannot be retired.', set: null };
  }
  if (options.retired) {
    return { kind: 'retired', audience: 'nobody', label: 'Retired', why: 'Retired: nobody can pick it until it is restored (Day outfits and retired outfits, below).', set: null };
  }
  const set = setOf(outfit, categories);
  const named = set ? { id: set.id, name: set.name } : null;
  if (outfit.kind === 'holiday' || outfit.kind === 'fun' || options.day) {
    const next = options.day?.nextDates[0];
    const date = next === undefined ? undefined : isoDate(next);
    const when = date ? dateWords(date, now, options.locale) : null;
    return {
      kind: 'everyone-on-day',
      audience: 'everyone',
      label: when ? `Everyone on its day, ${when}` : 'Everyone on its day',
      why: `A day outfit: everyone gets it on its day${when ? `, ${when},` : ''} with its greeting, and the principals can pick it any day.`,
      set: named,
    };
  }
  if (set && !set.enabled) {
    return {
      kind: 'set-off',
      audience: 'principals',
      label: `Principals only: ${set.name} is off`,
      why: `Its set, ${set.name}, is switched off, so only the principals can pick it. Switch the set on under Categories, below.`,
      set: named,
    };
  }
  const state = outfitState(outfit.window, now);
  if (state === 'ended') {
    return { kind: 'ended', audience: 'principals', label: 'Principals only: ended', why: 'Its date window has ended, so only the principals can pick it. Edit the window to bring it back.', set: named };
  }
  const season = set ? set.window : null;
  const seasonStart = set && season && !categoryLiveOn(set, monthDayNumber(today)) ? nextSeasonStart(season, today, now) : null;
  const shown = effectiveWindow(outfit.window, now);
  const windowStart = state === 'scheduled' && shown.kind === 'between' ? pacificDate(shown.startMs) : null;
  if (set && seasonStart && (!windowStart || compareDates(seasonStart, windowStart) >= 0)) {
    const until = dateWords(seasonStart, now, options.locale);
    const also = windowStart ? ` Its own date window starts ${dateWords(windowStart, now, options.locale)}.` : '';
    return {
      kind: 'set-out-of-window',
      audience: 'principals',
      label: `Principals only until ${until}`,
      why: `Its set, ${set.name}, is out of season until ${until} (${categoryWindowText(season)}), so only the principals can pick it until then.${also}`,
      set: named,
    };
  }
  if (windowStart) {
    const until = dateWords(windowStart, now, options.locale);
    const also = set && seasonStart ? ` Its set, ${set.name}, is also out of season until ${dateWords(seasonStart, now, options.locale)}.` : '';
    return { kind: 'scheduled', audience: 'principals', label: `Principals only until ${until}`, why: `Its date window starts ${until}, so only the principals can pick it until then.${also}`, set: named };
  }
  const why = set
    ? `Its set, ${set.name}, is ${season ? `in season (${categoryWindowText(season)})` : 'switched on'} and its date window is on now, so everyone can pick it.`
    : 'It is in General, which is always on, and its date window is on now, so everyone can pick it.';
  return { kind: 'everyone', audience: 'everyone', label: 'Everyone now', why, set: named };
}

export interface VisibilityGroup {
  /** "everyone", "season:<set id>", "off", "scheduled:<window key>" or "ended:<window key>". */
  key: string;
  bucket: OutfitBucket;
  /** The heading: who can pick these now, and why. */
  title: string;
  /** A scheduled or ended group's shared date window (the card words it by its clock); null for the others. */
  window: MascotWindow | null;
  /** Catalog order. */
  outfits: AdminOutfit[];
}

export interface GalleryVisibility {
  byId: ReadonlyMap<string, OutfitVisibility>;
  /** An outfit's visibility: the one judged with the others, else judged now. */
  of: (outfit: AdminOutfit) => OutfitVisibility;
  /**
   * The everyone group first (empty when no outfit is everyone's: the built-in Gyro still is), then the principals-only
   * groups (one per set out of season, by the day it comes back, then the sets that are off as one), then scheduled
   * and ended, one group per date window, by its start.
   */
  groups: VisibilityGroup[];
  /** How many outfits each filter shows (the built-in Gyro not counted). */
  counts: Record<OutfitBucket, number>;
  /** "Everyone can pick 45 outfits now (sets on: NFL, Holiday - Halloween); 573 more are principals only." */
  summary: string;
}

const outfitCount = (value: number): string => `${value.toLocaleString('en-US')} outfit${value === 1 ? '' : 's'}`;

/** Up to six names as they are, else the first five and "and N more". */
function listText(names: ReadonlyArray<string>): string {
  if (names.length <= 6) return names.join(', ');
  return `${names.slice(0, 5).join(', ')} and ${names.length - 5} more`;
}

/** The one line at the top of the gallery: how many outfits everyone can pick now, which sets those are from, and how many only the principals can. */
function summaryLine(counts: Record<OutfitBucket, number>, setsOn: ReadonlyArray<string>, unreadable: boolean): string {
  const principals = counts.principals + counts.scheduled + counts.ended;
  const are = principals === 1 ? 'is' : 'are';
  let line = counts.everyone === 0
    ? `Everyone can pick only the built-in Gyro now; ${principals === 0 ? 'no outfits have been added' : `${outfitCount(principals)} ${are} principals only`}.`
    : `Everyone can pick ${outfitCount(counts.everyone)} now${setsOn.length > 0 ? ` (sets on: ${setsOn.join(', ')})` : ''}; ${principals === 0 ? 'none are' : `${principals.toLocaleString('en-US')} more ${are}`} principals only.`;
  if (unreadable) line += ' The sets could not be read just now, so this counts as if every set were on.';
  return line;
}

/**
 * The whole gallery judged once: each outfit's visibility, the grid's groups, the filter counts and the summary line.
 * `categories` null means the service could not read the sets (the summary says so); absent means a service before
 * sets. Day outfits and retired outfits are judged but belong to no group (the Day outfits section lists them).
 */
export function galleryVisibility(outfits: ReadonlyArray<AdminOutfit>, categories: CategoriesLike | null | undefined, today: string, now: number, locale?: string): GalleryVisibility {
  const byId = new Map<string, OutfitVisibility>();
  const everyone: VisibilityGroup = { key: 'everyone', bucket: 'everyone', title: 'Everyone can pick these now', window: null, outfits: [] };
  const seasons = new Map<string, { start: PacificDate; group: VisibilityGroup }>();
  const off: VisibilityGroup = { key: 'off', bucket: 'principals', title: '', window: null, outfits: [] };
  const offSets = new Set<string>();
  const setsOn = new Set<string>();
  const spans = new Map<string, { start: number; group: VisibilityGroup }>();
  for (const outfit of outfits) {
    const visibility = outfitVisibility(outfit, categories, today, now, { locale });
    byId.set(outfit.id, visibility);
    if (visibility.kind === 'everyone') {
      everyone.outfits.push(outfit);
      if (visibility.set) setsOn.add(visibility.set.id);
    } else if (visibility.kind === 'set-off' && visibility.set) {
      off.outfits.push(outfit);
      offSets.add(visibility.set.id);
    } else if (visibility.kind === 'set-out-of-window' && visibility.set) {
      const set = visibility.set;
      let season = seasons.get(set.id);
      if (!season) {
        const window = categories?.categories.find((category) => category.id === set.id)?.window ?? null;
        const start = window ? nextSeasonStart(window, today, now) : pacificDate(now);
        season = { start, group: { key: `season:${set.id}`, bucket: 'principals', title: `${visibility.label}: the ${set.name} set is out of season`, window: null, outfits: [] } };
        seasons.set(set.id, season);
      }
      season.group.outfits.push(outfit);
    } else if (visibility.kind === 'scheduled' || visibility.kind === 'ended') {
      const key = `${visibility.kind}:${windowKey(outfit.window)}`;
      let span = spans.get(key);
      if (!span) {
        const shown = effectiveWindow(outfit.window, now);
        const title = visibility.kind === 'scheduled' ? `${visibility.label}; everyone from then` : 'Principals only: the date window has ended';
        span = { start: shown.kind === 'always' ? Number.NEGATIVE_INFINITY : shown.startMs, group: { key, bucket: visibility.kind, title, window: outfit.window, outfits: [] } };
        spans.set(key, span);
      }
      span.group.outfits.push(outfit);
    }
  }
  const namesOf = (ids: ReadonlySet<string>): string[] => (categories ? categories.categories.filter((category) => ids.has(category.id)).map((category) => category.name) : []);
  off.title = `Principals only: their sets are off (${listText(namesOf(offSets))})`;
  const byBucket = (bucket: OutfitBucket) => [...spans.values()].filter((span) => span.group.bucket === bucket).sort((a, b) => a.start - b.start).map((span) => span.group);
  const groups = [
    everyone,
    ...[...seasons.values()].sort((a, b) => compareDates(a.start, b.start)).map((season) => season.group),
    ...(off.outfits.length > 0 ? [off] : []),
    ...byBucket('scheduled'),
    ...byBucket('ended'),
  ];
  // The filter counts are the groups' sizes: a day outfit or a retired one is judged but shown in no group.
  const counts: Record<OutfitBucket, number> = { everyone: 0, principals: 0, scheduled: 0, ended: 0 };
  for (const group of groups) counts[group.bucket] += group.outfits.length;
  return {
    byId,
    of: (outfit) => byId.get(outfit.id) ?? outfitVisibility(outfit, categories, today, now, { locale }),
    groups,
    counts,
    summary: summaryLine(counts, namesOf(setsOn), categories === null),
  };
}
